import React, { useState, ReactNode } from 'react';
import deepEqual from 'deep-equal';

// Components
import Table from '@components/UI-KIT/Table';
import AmountFormat from '@components/utils/AmountFormat';
import Typography from '@components/UI-KIT/Typography';
import TransactionDetails from '@components/Accounts/TransactionDetails';
import TransactionsTableFilters from '@components/Accounts/TransactionsTable/Filters';

// Styles
import VARIABLES from '@/assets/variables';
import {
    Income,
    Outgoing,
    SnackbarSuccess,
    SnackbarError
} from '@/assets/icons';

// Entities
import { downloadExcelForAccountTransactionBalance, Transaction, TransactionsFiltersDefault } from '@services/account-transaction';
import { AttachedProof } from '@/assets/entities';
import { Account } from '@/src/services/account';

// Services
import { TransactionsFilters, deleteAccountTransaction, addVoucher } from '@services/account-transaction';
import DownloadAccountBalanceExcelModal from './DownloadAccountBalanceExcelModal';

interface Props {
    accounts: Account[];
    table: {
        page: number;
        size: number;
    };
    setTable: React.Dispatch<React.SetStateAction<{
        page: number;
        size: number;
    }>>;
    filters: TransactionsFilters;
    setFilters: React.Dispatch<React.SetStateAction<TransactionsFilters>>;
    transactions: {
        items: Transaction[];
        totalItems: number;
    };
    transactionStatus: string;
    handleFetchAccountTransactions: (_value?:string) => void;
    handleFetchOrganizationAccounts: () => void;
    handleFetchHeroData: () => void;
    search: string;
    setSearch: React.Dispatch<React.SetStateAction<string>>;
    setTransactionStatus: React.Dispatch<React.SetStateAction<string>>;
    setDrawer: React.Dispatch<React.SetStateAction<{
        drawerOpened: boolean;
        drawerTitle: string;
        titleIcon: any;
        drawerChildren: any;
        drawerFooter: boolean;
        drawerClass?: string;
    }>>;
    snackbarContext: {
        setSnackbar: (_snackbar: {
            snackbarMessage: string;
            snackbarIcon: React.ReactNode;
            snackbarColor: 'neutral' | 'success' | 'error';
        }) => void;
    };
    setModal: React.Dispatch<React.SetStateAction<{
        show: boolean;
        text: string;
        snackbar: {
            success: string;
            error: string;
        },
        action: () => void;
    }>>;
    filtersApplied: boolean;
    title: string;
    emptyState: ReactNode;
}

const TransactionsTable: React.FC<Props> = ({
    filtersApplied,
    accounts,
    table,
    setTable,
    filters,
    setFilters,
    transactions,
    transactionStatus,
    setTransactionStatus,
    handleFetchAccountTransactions,
    handleFetchOrganizationAccounts,
    handleFetchHeroData,
    search,
    setSearch,
    setDrawer,
    snackbarContext,
    setModal,
    title,
    emptyState
}) => {
    const [ transactionId, setTransactionId ] = useState(0);
    const [ openDownloadAccountBalanceExcelModal, setOpenDownloadAccountBalanceExcelModal ] = useState(false);

    const hiddenFileInput = React.useRef(null);

    const handleClick = item => {
        hiddenFileInput.current.click();
        setTransactionId(item.id);
    };

    const handleChange = async event => {
        const fileUploaded = event.target.files[0];
        try {
            await addVoucher(transactionId, fileUploaded);

            snackbarContext.setSnackbar({
                snackbarMessage: 'Comprobante adjuntado con éxito',
                snackbarIcon: (
                    <SnackbarSuccess />
                ),
                snackbarColor: 'success'
            });
        } catch(ex) {
            snackbarContext.setSnackbar({
                snackbarMessage: 'Hubo un error adjuntando el comprobante',
                snackbarIcon: (
                    <SnackbarError />
                ),
                snackbarColor: 'error'
            });
        } finally {
            handleFetchAccountTransactions();
        }
    };

    const handleDownloadAttach = async (att: AttachedProof) => {
        const a = document.createElement('a');
        a.href = att.url;
        a.download = att.originalname;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const deleteTransaction = async (transactionId: number) => {
        let requestStatus = 0;

        try {
            const { status } = await deleteAccountTransaction(transactionId);
            requestStatus = status;
        } finally {
            if (requestStatus !== 200) {
                return false;
            }
        }

        handleFetchAccountTransactions();
        handleFetchOrganizationAccounts();
        handleFetchHeroData();

        return true;
    };

    const [ disableDownloadExcelButton, setDisableDownloadExcelButton ] = useState(false);
    const handleDownloadExcelLink = async () => {
        setDisableDownloadExcelButton(true);
        const { status, data } = await downloadExcelForAccountTransactionBalance(0, 0, search, filters);

        if (status === 200) {
            snackbarContext.setSnackbar({
                snackbarMessage: 'Se ha generado el archivo de excel',
                snackbarIcon: (
                    <SnackbarSuccess />
                ),
                snackbarColor: 'success'
            });
            const { url } = data;
            setDisableDownloadExcelButton(false);
            return window.open(url, '_blank');
        }
        else {
            snackbarContext.setSnackbar({
                snackbarMessage: 'Hubo un error generando el archivo de excel',
                snackbarIcon: (
                    <SnackbarError />
                ),
                snackbarColor: 'error'
            });
            setDisableDownloadExcelButton(false);
        }

    };

    const showEmptyState = transactionStatus === 'ready'
                        && transactions.totalItems === 0
                        && search === ''
                        && deepEqual(filters, TransactionsFiltersDefault());

    return (
        <>
            { showEmptyState ? (
                <>
                    <Typography
                        variant="h3"
                        color="secondary"
                    >
                        {title}
                    </Typography>
                    {emptyState}
                </>
            ) : (
                <>
                    <Table
                        filters={(
                            <TransactionsTableFilters
                                accounts={accounts}
                                filters={filters}
                                setFilters={setFilters}
                                resetFilters={() => {
                                    setTransactionStatus('pending');
                                    setFilters(TransactionsFiltersDefault());
                                }}
                                handleFetchData={() => {
                                    setTable(prevState => ({
                                        ...prevState,
                                        page: 1
                                    }));
                                    handleFetchAccountTransactions();
                                }}
                            />
                        )}
                        pagination
                        filtersApplied={filtersApplied}
                        title="Mis transacciones"
                        downloadExcel={() => setOpenDownloadAccountBalanceExcelModal(true)}
                        disableDownloadExcel={disableDownloadExcelButton}
                        page={table.page}
                        size={table.size}
                        fetchStatus={transactionStatus}
                        searchbar={{
                            action: (value: string) => setSearch(value),
                            placeholder: 'Buscar...',
                            term: search
                        }}
                        handleSizeChange={(value: number) => setTable(prevState => ({
                            ...prevState,
                            size: value
                        }))}
                        handlePageChange={(value: number) => setTable(prevState => ({
                            ...prevState,
                            page: value
                        }))}
                        tableData={transactions}
                        tableHead={[
                            {
                                label: 'Tipo',
                                type: 'custom',
                                handleValue: (item: Transaction) => (
                                    item.amount >= 0
                                        ? <Income />
                                        : <Outgoing />
                                )
                            },
                            {
                                type: 'custom',
                                label: 'Monto',
                                handleValue: (item: Transaction) => (
                                    <label
                                        style={{
                                            color: item.amount >= 0
                                                ? VARIABLES.palette.success_color
                                                : VARIABLES.palette.error_color
                                        }}
                                    >
                                        {item.amount >= 0
                                            ? '+'
                                            : ''
                                        }
                                        <AmountFormat
                                            value={item.amount}
                                        />
                                    </label>
                                )
                            },
                            {
                                label: 'Cuenta',
                                fields: [ 'account', 'name' ],
                                type: 'nested'
                            },
                            {
                                label: 'Fecha',
                                field: 'date',
                                format: 'date'
                            },
                            {
                                label: 'Concepto',
                                type: 'nested',
                                fields: [ 'concept', 'name' ]
                            },
                            {
                                label: 'Propiedad',
                                type: 'custom',
                                handleValue: (item: Transaction) => `${item.property ? `Carpeta ${item.property.externalReference} - ${item.property.address.street}` : '-'}`
                            }
                        ]}
                        actions={[
                            {
                                type: 'custom',
                                label: 'Ver detalles',
                                actionHandler: (item: Transaction) => setDrawer({
                                    drawerOpened: true,
                                    drawerTitle: 'Detalles de la transacción',
                                    titleIcon: null,
                                    drawerClass: 'AOL-detail-drawer-children',
                                    drawerChildren: (
                                        <TransactionDetails
                                            transaction={item}
                                            handleDeleteTransaction={() => {
                                                setTransactionId(item.id);
                                                setModal({
                                                    show: true,
                                                    text: '¿Deseas eliminar la transacción? Esta acción no se puede deshacer.',
                                                    snackbar: {
                                                        error: 'Hubo un error eliminando la transacción',
                                                        success: 'Transferencia eliminada'
                                                    },
                                                    action: () => deleteTransaction(item.id)
                                                });
                                            }}
                                        />
                                    ),
                                    drawerFooter: true
                                })
                            },
                            {
                                type: 'custom',
                                label: 'Adjuntar comprobante',
                                limit: true,
                                condition: (item: Transaction) => item.attachProofIds.length === 0,
                                actionHandler: item => handleClick(item)
                            },
                            {
                                type: 'custom',
                                label: 'Descargar comprobante',
                                limit: true,
                                condition: (item: Transaction) => item.attachProofIds.length > 0,
                                actionHandler: (item: Transaction) => handleDownloadAttach(item.attachProofIds[0])
                            },
                            {
                                type: 'custom',
                                label: 'Eliminar transacción',
                                color: 'error',
                                limit: true,
                                condition: (item: Transaction) => item.isDeletable,
                                actionHandler: item => {
                                    setTransactionId(item.id);
                                    setModal({
                                        show: true,
                                        text: '¿Deseas eliminar la transacción? Esta acción no se puede deshacer.',
                                        snackbar: {
                                            error: 'Hubo un error eliminando la transacción',
                                            success: 'Transferencia eliminada'
                                        },
                                        action: () => deleteTransaction(item.id)
                                    });
                                }
                            }
                        ]}
                    />
                    <input
                        type="file"
                        ref={hiddenFileInput}
                        onChange={handleChange}
                        style={{ display: 'none' }}
                    />
                </>
            )}
            <DownloadAccountBalanceExcelModal
                openModal={openDownloadAccountBalanceExcelModal}
                setOpenModal={setOpenDownloadAccountBalanceExcelModal}
                disableModalButtons={disableDownloadExcelButton}
                downloadExcel={handleDownloadExcelLink}
                filters={filters}
                setFilters={setFilters}
            />
        </>
    );
};

export default TransactionsTable;
